import React, { useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import { SUCCESS_THEN_REDIRECT_MS } from '../utils/submitFeedback';
import {
  AUTOMOTIVE_BRANDS,
  AUTOMOTIVE_STOCK_CATEGORIES,
  AUTOMOTIVE_UNITS,
} from '../constants/automotiveStockCategories';
import './AutomotivePresetPage.css';

const CONFIG = {
  brands: {
    title: 'Brands',
    singular: 'Brand',
    storageKey: 'brands',
    defaults: AUTOMOTIVE_BRANDS,
    placeholder: 'e.g. Toyota, Denso, NGK',
  },
  units: {
    title: 'Units',
    singular: 'Unit',
    storageKey: 'units',
    defaults: AUTOMOTIVE_UNITS,
    placeholder: 'e.g. pcs, set, pair, litre',
  },
  categories: {
    title: 'Categories',
    singular: 'Category',
    storageKey: 'categories',
    defaults: AUTOMOTIVE_STOCK_CATEGORIES,
    placeholder: 'e.g. Brake System, Motorcycle Parts',
  },
};

function normalizeText(v) {
  return String(v || '').trim();
}

function optionFromValue(value, system = false) {
  if (typeof value === 'string') {
    return {
      name: value,
      value,
      code: '',
      status: 'Active',
      description: '',
      system,
    };
  }
  const name = normalizeText(value?.name || value?.label || value?.value);
  return {
    name,
    value: name,
    code: normalizeText(value?.code || ''),
    status: normalizeText(value?.status || 'Active') || 'Active',
    description: normalizeText(value?.description || ''),
    system: Boolean(value?.system || system),
  };
}

function mergeOptions(defaults, custom, removed = []) {
  const map = new Map();
  const removedSet = new Set((removed || []).map((x) => normalizeText(x).toLowerCase()).filter(Boolean));
  [...(defaults || []).map((x) => optionFromValue(x, true)), ...(custom || []).map((x) => optionFromValue(x, false))]
    .filter((x) => x.name)
    .filter((x) => !x.system || !removedSet.has(x.name.toLowerCase()))
    .forEach((item) => {
      const key = item.name.toLowerCase();
      if (!map.has(key) || !item.system) map.set(key, item);
    });
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export default function AutomotivePresetPage({ type = 'brands', onNavigate }) {
  const { user } = useAuth();
  const cfg = CONFIG[type] || CONFIG.brands;
  const tenantId = user?.tenant?.id || user?.tenantId || 'auto-default';
  const storageKey = `cbonline.auto-presets.${tenantId}.${cfg.storageKey}`;
  const removedStorageKey = `${storageKey}.removed`;
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('list');
  const [editingName, setEditingName] = useState('');
  const [form, setForm] = useState({ name: '', code: '', status: 'Active', description: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [customRows, setCustomRows] = useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [removedRows, setRemovedRows] = useState(() => {
    try {
      const raw = window.localStorage.getItem(removedStorageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const rows = useMemo(() => mergeOptions(cfg.defaults, customRows, removedRows), [cfg.defaults, customRows, removedRows]);
  const filteredRows = useMemo(() => {
    const q = normalizeText(search).toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.code, r.status, r.description].some((v) => normalizeText(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const persist = (next) => {
    setCustomRows(next);
    window.localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const persistRemoved = (next) => {
    setRemovedRows(next);
    window.localStorage.setItem(removedStorageKey, JSON.stringify(next));
  };

  const openCreate = () => {
    setEditingName('');
    setForm({ name: '', code: '', status: 'Active', description: '' });
    setSuccessMessage('');
    setMode('form');
  };

  const openEdit = (row) => {
    setEditingName(row.name);
    setForm({
      name: row.name,
      code: row.code || '',
      status: row.status || 'Active',
      description: row.description || '',
    });
    setSuccessMessage('');
    setMode('form');
  };

  const remove = (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    const rowKey = row.name.toLowerCase();
    persist(customRows.filter((r) => optionFromValue(r).name.toLowerCase() !== rowKey));
    if (row.system) {
      persistRemoved([...new Set([...removedRows, row.name])]);
    }
    setSuccessMessage(`✓ "${row.name}" removed from ${cfg.title.toLowerCase()}.`);
  };

  const submit = (e) => {
    e.preventDefault();
    const name = normalizeText(form.name);
    if (!name) return;
    const nextRow = {
      name,
      value: name,
      label: name,
      code: normalizeText(form.code),
      status: normalizeText(form.status) || 'Active',
      description: normalizeText(form.description),
    };
    const editingKey = editingName.toLowerCase();
    const nextRemoved = removedRows.filter((r) => {
      const key = normalizeText(r).toLowerCase();
      return key !== name.toLowerCase() && key !== editingKey;
    });
    const wasEditingDefault = editingName && (cfg.defaults || []).some((item) => optionFromValue(item, true).name.toLowerCase() === editingKey);
    if (wasEditingDefault && editingKey !== name.toLowerCase()) {
      nextRemoved.push(editingName);
    }
    const withoutOld = customRows.filter((r) => {
      const rowName = optionFromValue(r).name.toLowerCase();
      return rowName !== name.toLowerCase() && rowName !== editingName.toLowerCase();
    });
    persistRemoved([...new Set(nextRemoved)]);
    persist([...withoutOld, nextRow]);
    setSuccessMessage(
      editingName
        ? `✓ "${name}" updated successfully.`
        : `✓ "${name}" added to ${cfg.title.toLowerCase()}.`
    );
    setMode('list');
    window.setTimeout(() => setSuccessMessage(''), SUCCESS_THEN_REDIRECT_MS + 800);
  };

  return (
    <div className="auto-preset page-bleed">
      <header className="auto-preset__head">
        <div>
          <h1>{mode === 'form' ? `${editingName ? 'Edit' : 'New'} ${cfg.singular}` : cfg.title}</h1>
          <p>{mode === 'form' ? cfg.title : 'Manage starter values and your custom entries.'}</p>
        </div>
        {mode === 'form' ? (
          <button type="button" className="auto-preset__secondary" onClick={() => setMode('list')}>
            Back to {cfg.title}
          </button>
        ) : (
          <button type="button" className="auto-preset__primary" onClick={openCreate}>
            + New {cfg.singular}
          </button>
        )}
      </header>

      {successMessage ? (
        <div className="auto-preset__success" role="status">
          {successMessage}
        </div>
      ) : null}

      {mode === 'form' ? (
        <form className="auto-preset__panel" onSubmit={submit}>
          <h2>{cfg.singular} Details</h2>
          <div className="auto-preset__form-grid">
            <label>
              <span>{cfg.singular} name</span>
              <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder={cfg.placeholder} required />
            </label>
            <label>
              <span>Code</span>
              <input value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} placeholder="No code" />
            </label>
            <label>
              <span>Status</span>
              <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </label>
            <label className="auto-preset__field-wide">
              <span>Description</span>
              <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={4} />
            </label>
          </div>
          <div className="auto-preset__actions">
            <button type="button" className="auto-preset__secondary" onClick={() => setMode('list')}>
              Cancel
            </button>
            <button type="submit" className="auto-preset__primary">
              {editingName ? 'Save Changes' : `New ${cfg.singular}`}
            </button>
          </div>
        </form>
      ) : (
        <section className="auto-preset__panel">
          <div className="auto-preset__toolbar">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" />
            <button type="button" className="auto-preset__primary" onClick={() => setSearch('')}>
              Reset
            </button>
          </div>
          <div className="auto-preset__table-wrap">
            <table className="auto-preset__table">
              <thead>
                <tr>
                  <th>{cfg.singular}</th>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={`${row.system ? 'system' : 'custom'}-${row.name}`}>
                    <td>
                      <strong>{row.name}</strong>
                      <span>{row.description || (row.system ? 'Starter value' : 'No description')}</span>
                    </td>
                    <td>{row.code || 'No code'}</td>
                    <td>{row.status || 'Active'}</td>
                    <td>
                      <div className="auto-preset__row-actions">
                        <button type="button" onClick={() => openEdit(row)}>Edit</button>
                        <button type="button" onClick={() => remove(row)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
